import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import PDFPreview from "../documents/PDFPreview";
import { MButton } from "@/components/ui/atoms/MButtons";
import { useDocument } from "@/hooks/useDocument";
import { getMemberInfo } from "@/hooks/useMembers";
import { RootState } from "@/store";
import MSpacer from "@/components/ui/atoms/MSpacer";
import MLoader from "@/components/ui/atoms/MLoader";
import {
  triggerNativeEmergencyShare,
  triggerNativeWalletCardShare,
} from "@/utils/nativeBridge";
import { trackUserDataLoaded } from "@/utils/gtm";
import {
  ANALYTICS_SCREEN_TYPES,
  ANALYTICS_SCREEN_NAMES,
} from "@/constants/analytics.constants";
import { IconChevronLeft, IconShare3, IconDownload } from "@tabler/icons-react";
import { useIsDesktopPWA } from "@/hooks/useIsDesktopPWA";

const EmergencyCardViewer: React.FC = () => {
  const { t } = useTranslation();
  const isDesktop = useIsDesktopPWA();
  const location = useLocation();
  const navigate = useNavigate();
  const memberInfo = getMemberInfo();
  const selectedMemberFromRedux = useSelector(
    (state: RootState) => state.auth.selectedMember
  );

  // Get memberId from: location state > Redux selectedMember > logged-in member
  const memberIdFromState = location.state?.memberId;

  const memberId =
    memberIdFromState || selectedMemberFromRedux?.id || memberInfo.id;
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { downloadEmergencyCardPdf } = useDocument(memberId || "");

  // Track page load once
  useEffect(() => {
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.EMERGENCY_CARD,
      screenName: ANALYTICS_SCREEN_NAMES.EMERGENCY_CARD_VIEW,
    });
  }, []);

  useEffect(() => {
    if (!memberId) return;
    let cancelled = false;
    setLoading(true);
    let url: string | null = null;
    const fetchAll = async () => {
      try {
        const blob = await downloadEmergencyCardPdf();
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setPdfUrl(url);
      } catch (e: any) {
        if (!cancelled) console.error("Failed to load emergency card PDF", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchAll();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [memberId]);

  const isNative =
    typeof window !== "undefined" && !!(window as any).ReactNativeWebView;

  const handleNativeShare = async () => {
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.EMERGENCY_CARD,
      screenName: ANALYTICS_SCREEN_NAMES.EMERGENCY_CARD_SHARE,
    });

    if (isDesktop && pdfUrl) {
      const a = document.createElement("a");
      a.href = pdfUrl;
      a.download = `emergency-card-${memberId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      return;
    }

    if (!isNative) return;

    try {
      triggerNativeEmergencyShare({
        memberId,
        memberName: memberInfo.firstName,
      });
    } catch (e) {
      console.error("[EmergencyCardViewer] Error triggering native share", e);
    }
  };

  const handleWalletCardShare = async () => {
    if (!isNative) {
      console.log("[EmergencyCardViewer] wallet card clicked (non-native)", {
        memberId,
        memberName: memberInfo.firstName,
      });
      return;
    }

    try {
      triggerNativeWalletCardShare({
        memberId,
        memberName: memberInfo.firstName,
      });
    } catch (e) {
      console.error(
        "[EmergencyCardViewer] Error triggering wallet card share",
        e
      );
    }
  };

  const handleBack = () => {
    if (location.state?.backUrl) {
      navigate(location.state.backUrl);
      return;
    }

    navigate(-1);
  };

  return (
    <div className="flex flex-col items-center w-full min-h-screen">
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 w-full bg-white">
        <div className="relative flex items-center justify-center px-4 py-4">
          <button
            type="button"
            onClick={handleBack}
            className="absolute left-4 flex items-center gap-2 text-[32px] font-medium text-neutral-900"
          >
            <IconChevronLeft size={20} stroke={2.2} />
            <span className="text-[16px]">Back</span>
          </button>
          <div className="heading-h7-bold" style={{ fontSize: "16px" }}>
            Emergency Card
          </div>
        </div>
      </div>

      <div className="w-full max-w-2xl mx-auto ">
        {loading && <MLoader />}
        {pdfUrl && (
          <PDFPreview
            fileUrl={pdfUrl}
            controlsPosition="bottom"
            variant="emergency-card"
          />
        )}
      </div>
      <div className="dashboardEmergencyShareContainer confirm-footer w-[50%] m-auto">
        {!isDesktop && (
          <MButton
            variant="outline"
            size="md"
            onClick={handleWalletCardShare}
            leftIcon={<IconShare3 size={16} />}
          >
            {t("emergencyCard.walletCardButton")}
          </MButton>
        )}
        <MButton
          variant="filled"
          size="md"
          onClick={handleNativeShare}
          leftIcon={
            isDesktop ? <IconDownload size={16} /> : <IconShare3 size={16} />
          }
        >
          {isDesktop
            ? t("common.downloadPdf")
            : t("emergencyCard.sharePdfButton")}
        </MButton>
      </div>
    </div>
  );
};

export default EmergencyCardViewer;
